import { APP_URLS } from '@milliswift/api-client'
import { useEffect } from 'react'
import { Navigate, useLocation } from 'react-router-dom'
import type { User } from '@milliswift/types'
import { useAuth } from './AuthProvider'

function buildSignInUrl(returnTo: string): string {
  return `${APP_URLS.accounts}/login?return_to=${encodeURIComponent(returnTo)}`
}

/* ==========================================================================
 * RequireAuth
 * ======================================================================== */

export interface RequireAuthProps {
  children: React.ReactNode
  /**
   * Narrow access to the given roles. Checks the user's org role when one is
   * present; falls back to granting access, since role data arrives separately
   * from the session. The console itself is single-tenant in this pass.
   */
  roles?: string[]
  /** Rendered while the session is being restored from the cookie. */
  loading?: React.ReactNode
}

/**
 * Route guard. Waits for session restoration rather than bouncing to sign-in,
 * so a reload on an authenticated page does not flash the login screen.
 */
export function RequireAuth({ children, roles, loading }: RequireAuthProps) {
  const { status, user } = useAuth()
  const location = useLocation()

  if (status === 'loading') {
    return <>{loading ?? null}</>
  }

  if (status === 'anonymous') {
    return <Navigate to={buildSignInUrl(location.pathname + location.search)} replace />
  }

  if (roles && roles.length > 0) {
    const role = (user as (User & { org_role?: string }) | null)?.org_role
    // Without role data we let the request through; the API enforces the real
    // boundary and returns 403, which surfaces as an error toast.
    if (role && !roles.includes(role)) {
      return <Navigate to="/forbidden" replace />
    }
  }

  return <>{children}</>
}

/* ==========================================================================
 * RequireGuest
 * ======================================================================== */

export interface RequireGuestProps {
  children: React.ReactNode
  /** Where a signed-in user is sent instead. Defaults to the console. */
  redirectTo?: string
}

/**
 * The inverse guard, for sign-in and sign-up. A user who is already
 * authenticated has no business on these pages, so they are forwarded to the
 * app they came from when the accounts app sets that, or to the console.
 */
export function RequireGuest({ children, redirectTo = '/dashboard' }: RequireGuestProps) {
  const { status, isAuthenticated } = useAuth()
  const location = useLocation()

  const returnTo = new URLSearchParams(location.search).get('return_to')

  if (status === 'loading') return <>{children}</>

  if (isAuthenticated) {
    const target = returnTo ?? redirectTo
    // Cross-app targets need a full page navigation, not a router transition.
    if (/^https?:\/\//.test(target)) {
      // Render nothing for one frame; <CrossAppRedirect/> performs the move.
      return <CrossAppRedirect target={target} />
    }
    return <Navigate to={target} replace />
  }

  return <>{children}</>
}

/**
 * A one-frame component whose only job is a full-page navigation.
 * Kept as a component rather than a raw window.location assignment in render,
 * so the navigation happens in an effect — assigning location during render is
 * a side effect and breaks concurrent rendering.
 */
function CrossAppRedirect({ target }: { target: string }) {
  useEffect(() => {
    window.location.href = target
  }, [target])

  return null
}

/* ==========================================================================
 * Cross-app redirect helpers
 * ======================================================================== */

/** Send the browser to another milliswift app. */
export function redirectToApp(app: keyof typeof APP_URLS, path = '/'): void {
  window.location.href = `${APP_URLS[app]}${path}`
}

/** The return_to target for "back to where you were" links. */
export function returnToHere(): string {
  return encodeURIComponent(window.location.href)
}
